import React, { useState, useEffect } from 'react';
import { 
  getQuizScoreTextColorClass, 
  getCheatingProbabilityTextColorClass, 
  getCheatingProbabilityLabel
} from '../Utils/ColorUtils';
import QuestionnaireStatus from './Utils/QuestionnaireStatus';
import ChallengeStatus from './Utils/ChallengeStatus';

/**
 * EvalOverview Component
 * Reusable component for displaying evaluation overviews for both talents and candidates
 */
const EvalOverview = ({
  questionnaireData,
  challengeData,
  evalData,
  contextType = 'talent' // 'talent' or 'candidate'
}) => {
  // State to store parsed data
  const [parsedQuestionnaire, setParsedQuestionnaire] = useState({
    questions: [],
    completionDuration: null,
    maxDuration: null,
    status: null,
    cheatingEvents: [],
    cheatingAssessment: null,
    startDate: null,
    endDate: null,
    answeredQuestions: 0
  });
  
  const [parsedChallenge, setParsedChallenge] = useState({
    questions: [],
    completionDuration: null,
    maxDuration: null,
    status: null,
    cheatingEvents: [],
    cheatingAssessment: null,
    startDate: null,
    endDate: null,
    totalScore: null,
    answeredQuestions: 0
  });
  
  // Function to parse questionnaire data
  const prepareQuestionnaireData = () => {
    if (!questionnaireData) {
      return;
    }
    
    // Extract questions from questionnaire_final_json or questionnaire_copy_json if final is empty
    let questions = [];
    if (questionnaireData.questionnaire_final_json?.questions?.length > 0) {
      questions = questionnaireData.questionnaire_final_json.questions;
    } else if (questionnaireData.questionnaire_copy_json?.questions?.length > 0) {
      questions = questionnaireData.questionnaire_copy_json.questions;
    }
    
    // Calculate actual completion duration only if both start and end dates exist
    let completionDuration = null;
    if (questionnaireData.questionnaire_start_date && questionnaireData.questionnaire_end_date) {
      const startDate = new Date(questionnaireData.questionnaire_start_date);
      const endDate = new Date(questionnaireData.questionnaire_end_date);
      const durationMs = endDate - startDate;
      completionDuration = Math.round(durationMs / (1000 * 60)); // Convert to minutes
    }
    
    // Get max duration separately
    let maxDuration = null;
    if (questionnaireData.max_duration_minutes) {
      maxDuration = parseInt(questionnaireData.max_duration_minutes);
    }
    
    // Extract status
    const status = questionnaireData.questionnaire_status || null;
    
    // Extract cheating events
    const cheatingEvents = questionnaireData.questionnaire_cheating_events_json || [];
    
    // Extract cheating assessment
    const cheatingAssessment = questionnaireData.questionnaire_cheating_assessment_json || null;
    
    // Count answered questions
    let answeredQuestions = 0;
    if (questionnaireData.questionnaire_response_json) {
      answeredQuestions = questionnaireData.questionnaire_response_json.filter(q => 
        (q.answer && (Array.isArray(q.answer) ? q.answer.length > 0 : q.answer !== "")) || 
        (q.otherAnswer && q.otherAnswer !== "")
      ).length;
    }
    
    setParsedQuestionnaire({
      questions,
      completionDuration,
      maxDuration,
      status,
      cheatingEvents,
      cheatingAssessment,
      startDate: questionnaireData.questionnaire_start_date ? new Date(questionnaireData.questionnaire_start_date) : null,
      endDate: questionnaireData.questionnaire_end_date ? new Date(questionnaireData.questionnaire_end_date) : null,
      answeredQuestions
    });
  };
  
  // Function to parse challenge data
  const prepareChallengeData = () => {
    if (!challengeData) {
      return;
    }
    
    // Extract questions from challenge_final_json or challenge_copy_json if final is empty
    let questions = [];
    if (challengeData.challenge_final_json?.questions?.length > 0) {
      questions = challengeData.challenge_final_json.questions;
    } else if (challengeData.challenge_copy_json?.questions?.length > 0) {
      questions = challengeData.challenge_copy_json.questions;
    }
    
    // Count answered questions
    let answeredQuestions = 0;
    if (challengeData.challenge_response_json) {
      answeredQuestions = challengeData.challenge_response_json.filter(q => 
        (q.answer && (Array.isArray(q.answer) ? q.answer.length > 0 : q.answer !== "")) || 
        (q.otherAnswer && q.otherAnswer !== "")
      ).length;
    }
    
    // Calculate actual completion duration only if both start and end dates exist
    let completionDuration = null;
    if (challengeData.challenge_start_date && challengeData.challenge_end_date) {
      const startDate = new Date(challengeData.challenge_start_date);
      const endDate = new Date(challengeData.challenge_end_date);
      const durationMs = endDate - startDate;
      completionDuration = Math.round(durationMs / (1000 * 60)); // Convert to minutes
    }
    
    // Get max duration separately
    let maxDuration = null;
    if (challengeData.max_duration_minutes) {
      maxDuration = parseInt(challengeData.max_duration_minutes);
    }
    
    // Extract status
    const status = challengeData.challenge_status || null;
    
    // Extract cheating events
    const cheatingEvents = challengeData.challenge_cheating_events_json || [];
    
    // Extract cheating assessment
    const cheatingAssessment = challengeData.challenge_cheating_assessment_json || null;
    
    // Extract total score if available
    // const totalScore = challengeData.challenge_assessed_json?.totalScore || null;
    const totalScore = challengeData.challenge_final_json?.totalScore || null;
    
    setParsedChallenge({
      questions,
      completionDuration,
      maxDuration,
      status,
      cheatingEvents,
      cheatingAssessment,
      startDate: challengeData.challenge_start_date ? new Date(challengeData.challenge_start_date) : null,
      endDate: challengeData.challenge_end_date ? new Date(challengeData.challenge_end_date) : null,
      totalScore,
      answeredQuestions
    });
  };
  
  // Parse data when props change
  useEffect(() => {
    prepareQuestionnaireData();
    prepareChallengeData();
  }, [questionnaireData, challengeData]);
  
  // Format duration for display
  const formatDuration = (minutes) => {
    if (!minutes && minutes !== 0) return 'Not available';
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours === 0) {
      return `${minutes}m`;
    } else if (remainingMinutes === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${remainingMinutes}m`;
    }
  };
  
  // Format date for display
  const formatDate = (date) => {
    if (!date) return 'Not available';
    return date.toLocaleString();
  };
  
  // Helper function to extract numeric value from cheating probability
  const extractProbabilityValue = (probability) => {
    if (!probability && probability !== 0) return 0;
    // Convert to string first to handle both string and number inputs
    const probabilityString = String(probability);
    return parseInt(probabilityString.replace('%', ''));
  };
    
  return (
    <div>
      {/* Tab-related header */}
      <div className="bg-white shadow-sm rounded-lg p-6 mb-6 border border-gray-200">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Evaluation Overview</h2>
          <p className="text-gray-600 mt-2 text-lg">
            This tab provides a high-level overview of the selected evaluation for this {contextType}.
          </p>
        </div>
      </div>
      
      {/* Overview Content */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Questionnaire Overview */}
        <div className="bg-white shadow-md rounded-lg border border-gray-200 p-6 relative">
          {/* Overlay when questionnaire is unavailable */}
          {(!questionnaireData || parsedQuestionnaire.questions.length === 0) && (
            <div className="absolute inset-0 bg-gray-100 bg-opacity-90 backdrop-blur-sm rounded-lg flex items-center justify-center z-10">
              <div className="text-center">
                <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h4 className="text-lg font-semibold text-gray-700 mb-1">Questionnaire Unavailable</h4>
                <p className="text-sm text-gray-600">This evaluation does not include a questionnaire</p>
              </div>
            </div>
          )}
          
          <div className={(!questionnaireData || parsedQuestionnaire.questions.length === 0) ? 'filter blur-sm' : ''}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Questionnaire</h3>
              {parsedQuestionnaire.status && (
                <QuestionnaireStatus status={parsedQuestionnaire.status} />
              )}
            </div>
          
          <div className="space-y-4">
            {/* Questionnaire Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Questions</p>
                <p className="mt-1 text-lg font-semibold text-gray-900">
                  {parsedQuestionnaire.answeredQuestions} / {parsedQuestionnaire.questions.length}
                </p>
              </div>
              
              {parsedQuestionnaire.completionDuration !== null ? (
                <div>
                  <p className="text-sm font-medium text-gray-500">Completion Time</p>
                  <p className="mt-1 text-lg font-semibold text-gray-900">
                    {formatDuration(parsedQuestionnaire.completionDuration)}
                  </p>
                </div>
              ) : parsedQuestionnaire.maxDuration !== null ? (
                <div>
                  <p className="text-sm font-medium text-gray-500">Max Duration</p>
                  <p className="mt-1 text-lg font-semibold text-gray-900">
                    {formatDuration(parsedQuestionnaire.maxDuration)}
                  </p>
                </div>
              ) : null}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Start Date</p>
                <p className="mt-1 text-sm text-gray-900">
                  {parsedQuestionnaire.startDate ? formatDate(parsedQuestionnaire.startDate) : 'Not started yet'}
                </p>
              </div>
              
              <div>
                <p className="text-sm font-medium text-gray-500">End Date</p>
                <p className="mt-1 text-sm text-gray-900">
                  {parsedQuestionnaire.endDate ? formatDate(parsedQuestionnaire.endDate) : 'Not completed yet'}
                </p>
              </div>
            </div>
            
            {/* Integrity Risk Score */}
            {parsedQuestionnaire.cheatingAssessment && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-500">Integrity Risk Score</p>
                <p className="mt-1">
                  <span className={`font-bold text-lg ${getCheatingProbabilityTextColorClass(extractProbabilityValue(parsedQuestionnaire.cheatingAssessment.cheatingProbability))}`}>
                    {getCheatingProbabilityLabel(extractProbabilityValue(parsedQuestionnaire.cheatingAssessment.cheatingProbability))}
                  </span>
                </p>
              </div>
            )}
          </div>
          </div>
        </div>
        
        {/* Challenge Overview */}
        <div className="bg-white shadow-md rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">Challenge</h3>
            {parsedChallenge.status && (
              <ChallengeStatus status={parsedChallenge.status} />
            )}
          </div>
          
          <div className="space-y-4">
            {/* Challenge Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Questions</p>
                <p className="mt-1 text-lg font-semibold text-gray-900">
                  {parsedChallenge.answeredQuestions} / {parsedChallenge.questions.length}
                </p>
              </div>
              
              {parsedChallenge.completionDuration !== null ? (
                <div>
                  <p className="text-sm font-medium text-gray-500">Completion Time</p>
                  <p className="mt-1 text-lg font-semibold text-gray-900">
                    {formatDuration(parsedChallenge.completionDuration)}
                  </p>
                </div>
              ) : parsedChallenge.maxDuration !== null ? (
                <div>
                  <p className="text-sm font-medium text-gray-500">Max Duration</p>
                  <p className="mt-1 text-lg font-semibold text-gray-900">
                    {formatDuration(parsedChallenge.maxDuration)}
                  </p>
                </div>
              ) : null}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Start Date</p>
                <p className="mt-1 text-sm text-gray-900">
                  {parsedChallenge.startDate ? formatDate(parsedChallenge.startDate) : 'Not started yet'}
                </p>
              </div>
              
              <div>
                <p className="text-sm font-medium text-gray-500">End Date</p>
                <p className="mt-1 text-sm text-gray-900">
                  {parsedChallenge.endDate ? formatDate(parsedChallenge.endDate) : 'Not completed yet'}
                </p>
              </div>
            </div>
            
            {/* Score and Cheating Probability in 2-column layout */}
            <div className="grid grid-cols-2 gap-4 mt-4">
              {/* Only show score if it exists */}
              {parsedChallenge.totalScore ? (
                <div>
                  <p className="text-sm font-medium text-gray-500">Score</p>
                  <p className={`mt-1 text-2xl font-bold ${getQuizScoreTextColorClass(parsedChallenge.totalScore)}`}>
                    {`${parsedChallenge.totalScore}%`}
                  </p>
                </div>
              ) : <div></div>}
              
              {parsedChallenge.cheatingAssessment && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Integrity Risk Score</p>
                  <p className="mt-1">
                    <span className={`font-bold text-lg ${getCheatingProbabilityTextColorClass(extractProbabilityValue(parsedChallenge.cheatingAssessment.cheatingProbability))}`}>
                      {getCheatingProbabilityLabel(extractProbabilityValue(parsedChallenge.cheatingAssessment.cheatingProbability))}
                    </span>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Video Player Section */}
      {evalData?.intro_video_file_id && evalData?.signedURL && (
        <div className="bg-white shadow-md rounded-lg border border-gray-200 p-6 mb-6">
          <div className="mb-4">
            <h3 className="text-xl font-bold text-gray-900">Evaluation Video</h3>
            <p className="text-gray-600 mt-1">
              Introduction video of the {contextType}
            </p>
          </div>
          
          <div className="w-full">
            <video 
              controls 
              className="w-full max-w-4xl mx-auto rounded-lg shadow-sm"
              style={{ maxHeight: '500px' }}
            >
              <source src={evalData.signedURL} type="video/mp4" />
              <source src={evalData.signedURL} type="video/webm" />
              <source src={evalData.signedURL} type="video/ogg" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvalOverview;
